<template>
    <div class="main-container">
        <div class="expiredAlert text-center">
            <h1 class="fw-bold mb-3">Your trial has expired</h1>
            <p class="mb-4">Choose a plan to continue using Forkto</p>

            <div class="d-flex flex-column gap-3 mb-4">
                <button
                    v-if="canActivatePlan"
                    class="btn btn-light btn-lg fw-bold"
                    :disabled="isProcessing"
                    @click="handleActivatePlan"
                >
                    <span v-if="!isProcessing">
                        <i class="fas fa-rocket me-2"></i>
                        Activate your plan
                    </span>
                    <span v-else>
                        <span class="spinner-border spinner-border-sm me-2"></span>
                        Creating session...
                    </span>
                </button>
                <router-link
                    v-else
                    to="/settings/billing"
                    class="btn btn-light btn-lg fw-bold"
                >
                    <i class="fas fa-credit-card me-2"></i>
                    View billing
                </router-link>
            </div>

            <hr class="border-light opacity-50 my-4" />

            <p class="small mb-3">Need help? Contact us:</p>
            <div class="d-flex flex-wrap justify-content-center gap-3">
                <a
                    href="javascript:void(Tawk_API.toggle())"
                    class="btn btn-outline-light btn-sm"
                >
                    <i class="fas fa-comments me-1"></i>
                    Live Chat
                </a>
                <a href="tel:+441626242422" class="btn btn-outline-light btn-sm">
                    <i class="fas fa-phone me-1"></i>
                    +44 1626 242422
                </a>
                <a href="mailto:hi@forkto.com" class="btn btn-outline-light btn-sm">
                    <i class="fas fa-envelope me-1"></i>
                    hi@forkto.com
                </a>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { API } from "@/services/apiInstance";
import { getAPIErrorMessage, getAPIErrorStatus } from "@/services/apiErrors";
import { newIdempotencyKey } from '@/composables/globalMethods';

const store = useStore();
const instance = getCurrentInstance();
const isProcessing = ref(false);

const clientData = computed(() => store.state.client);

const hasPlanAssigned = computed(() => {
    return clientData.value?.stripe_price_id &&
           clientData.value?.stripe_price_id !== '';
});

const hasActiveSubscription = computed(() => {
    return clientData.value?.stripe_subscription_id &&
           clientData.value?.stripe_subscription_id !== '' &&
           clientData.value?.has_access === true;
});

const canActivatePlan = computed(() => {
    return hasPlanAssigned.value && !hasActiveSubscription.value;
});

const showError = (message) => {
    if (instance?.appContext?.config?.globalProperties?.$toast) {
        instance.appContext.config.globalProperties.$toast.error(message, { duration: 6000 });
    } else {
        alert(`Error: ${message}`);
    }
};

const handleActivatePlan = async () => {
    isProcessing.value = true;

    if (!clientData.value?.stripe_price_id) {
        showError('No plan has been assigned to your account. Please contact support.');
        isProcessing.value = false;
        return;
    }

    if (!store.state.token) {
        showError('You must be logged in to activate your plan.');
        isProcessing.value = false;
        return;
    }

    try {
        const checkoutUrl = await API.Client.CreateCheckoutSession(newIdempotencyKey());

        if (!checkoutUrl || typeof checkoutUrl !== 'string') {
            throw new Error('Invalid checkout URL received from server');
        }

        window.location.href = checkoutUrl;
    } catch (error) {
        console.error('[TrialExpiredAlert] Failed to create checkout session:', error);

        let errorMessage = 'Failed to start checkout process';

        const status = getAPIErrorStatus(error);
        if (status === 401 || status === 403) {
            errorMessage = 'Authentication failed. Please log in again.';
        } else {
            errorMessage = getAPIErrorMessage(error, errorMessage);
        }

        showError(errorMessage);
        isProcessing.value = false;
    }
};
</script>
